import React, { useState, useEffect } from 'react';
import { collection, addDoc, Timestamp, query, where, getDocs, updateDoc, arrayUnion, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { Input, Button, Card } from '../components/UI';

export default function ChildFormScreen({ user, navigate }) {
    const [name, setName] = useState('');
    const [dob, setDob] = useState('');
    const [gender, setGender] = useState('');
    const [nik, setNik] = useState('');
    const [nikType, setNikType] = useState('Anak');
    const [schoolName, setSchoolName] = useState('');
    const [className, setClassName] = useState('');
    const [schoolOptions, setSchoolOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchSchools = async () => {
            try {
                const snapshot = await getDocs(collection(db, 'children'));
                const schools = new Set();
                snapshot.docs.forEach(doc => {
                    const name = doc.data().schoolName;
                    if (name) schools.add(name.trim());
                });
                setSchoolOptions(Array.from(schools).sort());
            } catch (err) {
                console.error("Failed to fetch schools", err);
            }
        };
        fetchSchools();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name || !dob || !gender || !nik) {
            setError("Semua kolom bertanda * wajib diisi.");
            return;
        }
        setLoading(true);
        setError('');
        try {
            // Check if a child with this NIK already exists in the centralized collection
            const childrenRef = collection(db, 'children');
            const q = query(childrenRef, where("nik", "==", nik));
            const snapshot = await getDocs(q);

            const roleField = user.role === 'Tenaga Kesehatan' ? 'nakes' : user.role === 'Guru PAUD' ? 'teachers' : 'parents';

            if (!snapshot.empty) {
                // Child already exists! We just link them by adding the current user's UID to the appropriate role array.
                const existingChildDoc = snapshot.docs[0];
                const childRef = doc(db, 'children', existingChildDoc.id);
                
                await updateDoc(childRef, {
                    [roleField]: arrayUnion(user.uid)
                });
                console.log("Child linked successfully.");
            } else {
                // Child does not exist, create a new one
                const newChildData = {
                    name,
                    dob: Timestamp.fromDate(new Date(dob)),
                    gender,
                    nik,
                    nikType,
                    schoolName: schoolName || '',
                    className: className || '',
                    parents: user.role === 'Tenaga Kesehatan' || user.role === 'Guru PAUD' ? [] : [user.uid],
                    nakes: user.role === 'Tenaga Kesehatan' ? [user.uid] : [],
                    teachers: user.role === 'Guru PAUD' ? [user.uid] : [],
                    createdAt: Timestamp.now()
                };
                await addDoc(childrenRef, newChildData);
                console.log("Child created successfully.");
            }
            
            navigate('home');
        } catch (err) {
            console.error(err);
            setError("Gagal menyimpan data. Silakan coba lagi.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card>
            <form onSubmit={handleSubmit} className="space-y-4">
                <h2 className="text-xl font-bold text-center text-gray-800 mb-4">Informasi Data Anak</h2>
                {error && <p className="text-red-500 text-sm text-center">{error}</p>}
                <Input id="name" type="text" label="Nama Lengkap Anak *" value={name} onChange={e => setName(e.target.value)} required />
                <Input id="dob" type="date" label="Tanggal Lahir *" value={dob} onChange={e => setDob(e.target.value)} required />
                
                <div className="flex space-x-2">
                    <div className="flex-1">
                        <label htmlFor="nik" className="block text-gray-700 text-sm font-bold mb-2">NIK *</label>
                        <input id="nik" type="text" placeholder="16 Digit NIK" maxLength="16" value={nik} onChange={e => setNik(e.target.value.replace(/\D/g, ''))} className="input-field w-full" required />
                    </div>
                    <div className="w-1/3">
                        <label htmlFor="nikType" className="block text-gray-700 text-sm font-bold mb-2">Jenis NIK</label>
                        <select id="nikType" value={nikType} onChange={e => setNikType(e.target.value)} className="input-field w-full">
                            <option value="Anak">Anak</option>
                            <option value="Ibu">Ibu (Fallback)</option>
                        </select>
                    </div>
                </div>

                <div>
                    <label htmlFor="gender" className="block text-gray-700 text-sm font-bold mb-2">Jenis Kelamin *</label>
                    <select id="gender" value={gender} onChange={e => setGender(e.target.value)} className="input-field" required>
                        <option value="" disabled>Pilih Jenis Kelamin</option>
                        <option value="Laki-laki">Laki-laki</option>
                        <option value="Perempuan">Perempuan</option>
                    </select>
                </div>
                <div className="space-y-4 pt-2 border-t border-gray-100">
                    <div>
                        <Input id="schoolName" list="schoolOptionsList" type="text" label="Nama Sekolah / PAUD (Opsional)" value={schoolName} onChange={e => setSchoolName(e.target.value)} />
                        <datalist id="schoolOptionsList">
                            {schoolOptions.map((school, i) => (
                                <option key={i} value={school} />
                            ))}
                        </datalist>
                        <p className="text-[11px] text-gray-500 mt-1 ml-1 leading-tight">Sangat disarankan untuk diisi agar memudahkan Guru PAUD memantau perkembangan anak di sekolah.</p>
                    </div>
                    <div>
                        <Input id="className" type="text" label="Kelas (Opsional)" value={className} onChange={e => setClassName(e.target.value)} />
                        <p className="text-[11px] text-gray-500 mt-1 ml-1 leading-tight">Contoh: TK A, Kelompok Bermain, dll.</p>
                    </div>
                </div>
                <Button type="submit" disabled={loading}>{loading ? 'Menyimpan...' : 'Simpan Data Anak'}</Button>
            </form>
        </Card>
    );
}
